import { AuthFetchWithResponse } from 'src/Types/api'
import { ApiResult } from '../../api'
import { StatusCodes } from '../../http-status-codes'
import { handleFetchError } from 'src/Libs/api/utils'
import { ContactData as ContactDataDto } from 'src/Libs/user/models/user.model'
import { OwnerLocationOfInstallation } from '../models'
import { ApiAccountType } from 'src/Libs/api'
import { StorageModel as StorageDto } from 'src/State/Storage/StorageModel'
import { storageDtoToModelTransformer } from 'src/Libs/storage/transformers'
import { ownerOfInstallationDtoToModelTransformer } from 'src/Libs/installations/api/transformer'
import { StorageModel } from 'src/Libs/storage/models'
import { InstallationHistory } from '../models/installation-history.model'
import { InstallationHistoryDto } from '../dto/installation-history.dto'
import { fromDtoToInstallationHistoryConverter } from '../transformers/from-dto-to-installation-history.transformer'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { ChangeInstallationOwnerParams } from './types'
import { isFlowTheme } from 'src/Libs/utils'

export const fetchOwnerLocationOfInstallation = async (
  ownerId: number,
  ownerType: ApiAccountType,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<OwnerLocationOfInstallation>> => {
  try {
    const response = await authFetch<ContactDataDto>({
      path: ownerType === 'company' ? `users/companies/${ownerId}/location` : `users/${ownerId}/location`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      const data = ownerOfInstallationDtoToModelTransformer(response.data)

      return new ApiResult<OwnerLocationOfInstallation>(data)
    }

    return new ApiResult<OwnerLocationOfInstallation>(null, true)
  } catch (error) {
    return handleFetchError<OwnerLocationOfInstallation>(error)
  }
}

export const fetchStorageById = async (
  storageId: string | null | undefined,
  authFetch: AuthFetchWithResponse,
  log?: (logValue: string) => void
): Promise<ApiResult<StorageModel>> => {
  if (!storageId) return new ApiResult<StorageModel, AuthFetchError | Error>(null, true, new Error('No storageId provided'))

  try {
    const response = await authFetch<StorageDto>({
      path: `storages/${storageId}`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      const data = storageDtoToModelTransformer(response.data)

      return new ApiResult<StorageModel>(data)
    }

    log?.(`fetchStorageById. storageId: ${storageId}, response: ${JSON.stringify(response)}`)
    return new ApiResult<StorageModel>(null, true)
  } catch (error) {
    log?.(`fetchStorageById. storageId: ${storageId}, error: ${JSON.stringify(error)}`)
    return handleFetchError<StorageModel>(error)
  }
}

export const decommissionInstallation = async (
  installationId: number | string | undefined,
  payload: { reason: string },
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  const response = await authFetch<void>({
    path: `installations/${installationId}/decommission`,
    method: 'PUT',
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    payload,
  })

  if (response.status === StatusCodes.NO_CONTENT) {
    return new ApiResult<null>(null, false)
  }

  return new ApiResult<null>(null, true)
}

export const deleteInstallation = async (installationId: number | string | undefined, authFetch: AuthFetchWithResponse): Promise<ApiResult<null>> => {
  if (!installationId) {
    return new ApiResult<null>(null, true, new Error('No installationId provided'))
  }

  try {
    const response = await authFetch<void>({
      path: `installations/${installationId}`,
      method: 'DELETE',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<null>(null, false)
    }

    return new ApiResult<null>(null, true)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}

export const completeInstallation = async (
  installationId: number | string | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  if (!installationId) {
    return new ApiResult<null>(null, true, new Error('No installationId provided'))
  }

  try {
    const response = await authFetch<void>({
      path: `installations/${installationId}/complete`,
      method: 'POST',
      payload: {},
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<null>(null, false)
    }

    return new ApiResult<null>(null, true)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}

export const fetchHistory = async (
  installationId: string | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<InstallationHistory[]>> => {
  // disable this route since it doesn't work for flow yet
  if (isFlowTheme) return new ApiResult()
  if (!installationId) {
    return new ApiResult<InstallationHistory[]>(null, true, new Error('No installationId provided'))
  }

  try {
    const response = await authFetch<InstallationHistoryDto[]>({
      path: `installations/${installationId}/history`,
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const data = response.data.map(historyItem => fromDtoToInstallationHistoryConverter(historyItem))
      return new ApiResult<InstallationHistory[]>(data)
    }

    return new ApiResult<InstallationHistory[]>(null, true)
  } catch (error) {
    return handleFetchError<InstallationHistory[]>(error)
  }
}

export const changeInstallationOwner = async ({
  authFetch,
  installationId,
  newOwnerId,
  oldOwnerAccountLinkId,
  reason,
  startAt,
}: ChangeInstallationOwnerParams): Promise<ApiResult<void>> => {
  const response = await authFetch<void>({
    path: `installations/${installationId}/owner`,
    method: 'PUT',
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    payload: {
      oldOwnerAccountLinkId,
      newOwnerId,
      startAt,
      reason,
    },
  })

  if (response.status === StatusCodes.NO_CONTENT) {
    return new ApiResult<void>(null, false)
  }

  return new ApiResult<void>(null, true)
}
